import { useMergeRefs } from '@floating-ui/react';
import {
  Children,
  cloneElement,
  memo,
  type ReactElement,
  type Ref,
} from 'react';

import { usePopoverContext } from '../context';

type TriggerElement = ReactElement & { ref?: Ref<HTMLElement> };

type Props = {
  /**
   * The element that opens the popover. `Popover` renders this element as the
   * trigger rather than adding a button of its own, so the trigger keeps its
   * semantics, styling, and accessible name. It must be a focusable element,
   * and it must forward props and its ref to the DOM node it renders.
   */
  children: ReactElement;
};

export const PopoverTrigger = memo(({ children }: Props) => {
  const { getReferenceProps, setReference } = usePopoverContext();
  const trigger = Children.only(children) as TriggerElement;
  const ref = useMergeRefs([setReference, trigger.ref ?? null]);

  return cloneElement(
    trigger,
    getReferenceProps({ ref, ...trigger.props }) as Partial<
      typeof trigger.props
    >,
  );
});

PopoverTrigger.displayName = 'PopoverTrigger';
